const s = {
  form: { maxWidth: 900, margin: '0 auto' } as React.CSSProperties,
  section: {
    background: '#ffffff',
    padding: '22px 24px',
    borderRadius: 12,
    marginBottom: 16,
    border: '1px solid #e5e7eb',
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 18,
    marginTop: 0,
    color: '#111827',
    paddingBottom: 12,
    borderBottom: '1px solid #f3f4f6',
  } as React.CSSProperties,
  r: { display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' } as React.CSSProperties,
  c: { flex: '1 1 280px', minWidth: 0 } as React.CSSProperties,
  l: {
    display: 'block',
    marginBottom: 6,
    fontWeight: 600,
    fontSize: 13,
    color: '#374151',
  } as React.CSSProperties,
  i: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    fontSize: 14,
    transition: 'border-color 0.15s',
    outline: 'none',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,
  actions: {
    display: 'flex',
    gap: 12,
    justifyContent: 'flex-end',
    paddingTop: 8,
    flexWrap: 'wrap',
  } as React.CSSProperties,
  submitBtn: {
    padding: '11px 28px',
    background: '#0074d9',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  } as React.CSSProperties,
  cancelBtn: {
    padding: '11px 28px',
    background: '#ffffff',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  } as React.CSSProperties,
};
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { PageLayout } from './DesignSystem';
import API_URL from '../config/api';
import { apiFetch } from '../utils/apiFetch';
import { showToast } from '../utils/toast';

const UK_POSTCODE_RE = /^([A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}|GIR\s?0AA)$/i;
const UK_VAT_RE = /^(GB)?(\d{9}|\d{12}|(GD|HA)\d{3})$/i;

function formatTagsForInput(tags: unknown): string {
  if (Array.isArray(tags)) return tags.map(String).join(', ');
  if (typeof tags === 'string') {
    try {
      const parsed = JSON.parse(tags);
      if (Array.isArray(parsed)) return parsed.map(String).join(', ');
    } catch { /* plain string */ }
    return tags;
  }
  return '';
}

function parseTagsInput(input: string): string[] {
  return input.split(',').map(t => t.trim()).filter(Boolean);
}

function normalizeCustomerType(value: unknown): 'Retail' | 'Wholesale' | 'Walk-in' {
  const v = String(value || 'Retail');
  if (v === 'Wholesale' || v === 'Walk-in') return v;
  return 'Retail';
}

const CustomerForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [creditBalance, setCreditBalance] = useState<number>(0);
  const [attachments, setAttachments] = useState<{id:number;filename:string;url:string}[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    sicCode: '',
    companyNumber: '',
    vatNumber: '',
    notes: '',
    country: 'United Kingdom',
    city: '',
    address: '',
    postcode: '',
    digitalId: '',
    website: '',
    customerType: 'Retail' as 'Retail' | 'Wholesale' | 'Walk-in',
    pricingTier: '' as '' | 'A' | 'B' | 'C',
    status: 'Active' as 'Active' | 'Inactive',
    tagsInput: '',
    creditTerms: 30,
    creditLimit: 0,
    sendMonthlyStatement: false,
    statementEmailOverride: ''
  });
  const { token } = useAuth();

  useEffect(() => {
    if (isEdit && id) {
      setLoading(true);
      if (!token) {
        showToast('Please log in to view customer', 'warning');
        setLoading(false);
        navigate('/login');
        return;
      }

      setImageRemoved(false);
      apiFetch(`${API_URL}/customers/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then(async (res) => {
          if (!res.ok) throw new Error(`Status ${res.status}`);
          return res.json();
        })
        .then((data) => {
          // Postgres lowercases unquoted column names, so check both camelCase and lowercase variants
          setForm({
            fullName: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            companyName: data.companyName || data.companyname || '',
            sicCode: data.sicCode || data.siccode || '',
            companyNumber: data.companyNumber || data.companynumber || '',
            vatNumber: data.vat_number || data.vatNumber || '',
            notes: data.notes || '',
            country: data.country || 'United Kingdom',
            city: data.city || '',
            address: data.address || '',
            postcode: data.postcode || '',
            digitalId: data.digitalId || data.digital_id || '',
            website: data.website || '',
            customerType: normalizeCustomerType(data.customer_type || data.customerType || data.customertype),
            pricingTier: (['A', 'B', 'C'].includes(data.pricing_tier || data.pricingTier) ? (data.pricing_tier || data.pricingTier) : '') as '' | 'A' | 'B' | 'C',
            status: data.status === 'Inactive' ? 'Inactive' : 'Active',
            tagsInput: formatTagsForInput(data.tags),
            creditTerms: data.credit_terms || 30,
            creditLimit: parseFloat(data.credit_limit) || 0,
            sendMonthlyStatement: data.send_monthly_statement || false,
            statementEmailOverride: data.statement_email_override || ''
          });
          setCreditBalance(data.credit_balance || 0);
          setAttachments(Array.isArray(data.attachments) ? data.attachments : []);
          // refresh from server to catch any missing entries
          fetchAttachments().catch(() => {});
          if (data.image) {
            const url = data.image.startsWith('http') ? data.image : `${API_URL}${data.image}`;
            setImagePreview(url);
            setImageRemoved(false);
          }
        })
        .catch((err) => {
          console.error('Failed to load customer:', err);
          showToast('Failed to load customer', 'error');
        })
        .finally(() => setLoading(false));
    }
  }, [id, isEdit, token, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file', 'warning');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      showToast('Image must be smaller than 2MB', 'warning');
      return;
    }

    setImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageRemoved(true);
  };

  const fetchAttachments = async () => {
    if (!id || !token) return;
    const res = await apiFetch(`${API_URL}/customers/${id}/attachments`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) {
      const list = await res.json();
      setAttachments(Array.isArray(list) ? list : []);
    }
  };

  const uploadAttachment = async (file: File) => {
    if (!id || !token) return;
    const fd = new FormData();
    fd.append('file', file);
    const res = await apiFetch(`${API_URL}/customers/${id}/attachments`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: fd
    });
    if (res.ok) {
      // refresh from server in case URL formatting differs
      await fetchAttachments();
    } else {
      console.error('Attachment upload failed', await res.text());
      showToast('Failed to upload attachment', 'error');
    }
  };

  const deleteAttachment = async (attId: number) => {
    if (!id || !token) return;
    const res = await apiFetch(`${API_URL}/customers/${id}/attachments/${attId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) {
      setAttachments(prev => prev.filter(a => a.id !== attId));
    } else {
      showToast('Failed to delete attachment', 'error');
    }
  };

  const onFinish = async (e: React.FormEvent) => {
    e.preventDefault();

    const postcode = form.postcode.trim();
    if (postcode && !UK_POSTCODE_RE.test(postcode)) {
      showToast('Enter a valid UK postcode (e.g. SW1A 1AA)', 'warning');
      return;
    }

    const vat = form.vatNumber.trim().replace(/\s/g, '').toUpperCase();
    if (vat && !UK_VAT_RE.test(vat)) {
      showToast('Enter a valid UK VAT number (e.g. GB123456789)', 'warning');
      return;
    }

    try {
      setLoading(true);
      const url = isEdit ? `${API_URL}/customers/${id}` : `${API_URL}/customers`;
      const method = isEdit ? 'PUT' : 'POST';
      const formData = new FormData();
      formData.append('name', form.fullName);
      formData.append('email', form.email);
      if (form.phone) formData.append('phone', form.phone);
      if (form.companyName) formData.append('companyName', form.companyName);
      if (form.sicCode) formData.append('sicCode', form.sicCode);
      if (form.companyNumber) formData.append('companyNumber', form.companyNumber);
      formData.append('website', form.website.trim());
      if (vat) formData.append('vat_number', vat.startsWith('GB') ? vat : `GB${vat}`);
      if (form.notes) formData.append('notes', form.notes);
      formData.append('customerType', form.customerType);
      if (form.pricingTier) formData.append('pricingTier', form.pricingTier);
      formData.append('status', form.status);
      formData.append('tags', JSON.stringify(parseTagsInput(form.tagsInput)));
      if (form.country) formData.append('country', form.country);
      if (form.city) formData.append('city', form.city);
      if (form.address) formData.append('address', form.address);
      if (form.postcode) formData.append('postcode', form.postcode);
      if (form.digitalId) formData.append('digitalId', form.digitalId);
      formData.append('credit_terms', form.creditTerms.toString());
      formData.append('credit_limit', form.creditLimit.toString());
      formData.append('send_monthly_statement', form.sendMonthlyStatement.toString());
      if (form.statementEmailOverride) formData.append('statement_email_override', form.statementEmailOverride);
      if (imageFile) {
        formData.append('image', imageFile);
      } else if (isEdit && imageRemoved) {
        // Explicitly clear existing image on edit
        formData.append('image', '');
        formData.append('image_removed', 'true');
      }

      const response = await apiFetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => null);
        showToast(errBody?.message || errBody?.error || 'Failed to save customer', 'error');
        setLoading(false);
        return;
      }

      // Upload pending files for new customer
      if (!isEdit && pendingFiles.length > 0) {
        const newCustomer = await response.clone().json().catch(() => null);
        const newId = newCustomer?.id;
        if (newId) {
          for (const file of pendingFiles) {
            const fd = new FormData();
            fd.append('file', file);
            await apiFetch(`${API_URL}/customers/${newId}/attachments`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
              body: fd
            });
          }
        }
      }

      navigate('/customers/list', {
        state: { flash: `Customer ${isEdit ? 'updated' : 'created'} successfully` },
      });
    } catch (err) {
      showToast('Error saving customer', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout
      title={isEdit ? '✏️ Edit customer' : '👤 Add customer'}
      action={
        <button
          type="button"
          onClick={() => navigate(isEdit && id ? `/customers/view/${id}` : '/customers/list')}
          style={{
            padding: '10px 18px',
            background: 'linear-gradient(145deg, #374151 0%, #4b5563 45%, #6b7280 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 6px 16px rgba(55, 65, 81, 0.35)',
          }}
        >
          ← Back
        </button>
      }
    >
      <form onSubmit={onFinish} style={s.form}>
        {/* CREDIT BALANCE BANNER */}
        {isEdit && creditBalance > 0 && (
          <div style={{
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderLeft: '4px solid #059669',
            padding: '14px 18px',
            borderRadius: 10,
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Credit balance</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>£{Number(creditBalance || 0).toFixed(2)}</div>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/credit-notes?customer_id=${id}`)}
              style={{
                background: '#fff',
                color: '#059669',
                border: '1px solid #bbf7d0',
                padding: '8px 14px',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              View credit notes
            </button>
          </div>
        )}
        {/* BASIC INFORMATION */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Basic information</h3>
          {/* Image Upload */}
          <div style={{ marginBottom: 24 }}>
            <label style={s.l}>Profile Image</label>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
              {imagePreview && (
                <div style={{ position: 'relative' }}>
                  <img
                    src={imagePreview}
                    alt="Customer"
                    style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: '8px', border: '2px solid #e5e7eb' }}
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    style={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: 24,
                      height: 24,
                      cursor: 'pointer',
                      fontSize: 14,
                      fontWeight: 'bold'
                    }}
                  >
                    ×
                  </button>
                  <label style={{
                    display: 'block',
                    marginTop: 8,
                    fontSize: 12,
                    color: '#3b82f6',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontWeight: 600
                  }}>
                    Change photo
                    <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                  </label>
                </div>
              )}
              {!imagePreview && (
                <label style={{
                  width: 120,
                  height: 120,
                  border: '2px dashed #d1d5db',
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  background: '#f9fafb',
                  transition: 'all 0.3s'
                }}>
                  <span style={{ fontSize: 32, color: '#9ca3af' }}>📷</span>
                  <span style={{ fontSize: 12, color: '#666' }}>Upload Image</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                </label>
              )}
            </div>
            <p style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
              ℹ️ Supported formats: JPG, PNG, GIF. Max size: 2MB.
            </p>
          </div>
          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>Full Name *</label>
              <input
                type="text"
                name="fullName"
                value={form.fullName}
                onChange={handleChange}
                style={s.i}
                placeholder="Enter customer name"
                required
              />
            </div>
            <div style={s.c}>
              <label style={s.l}>Email *</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                style={s.i}
                placeholder="customer@example.com"
                required
              />
            </div>
          </div>
          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>Phone</label>
              <input
                type="text"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                style={s.i}
                placeholder="+44 123 456 7890"
              />
            </div>
            <div style={s.c}>
              <label style={s.l}>Digital ID</label>
              <input
                type="text"
                name="digitalId"
                value={form.digitalId}
                onChange={handleChange}
                style={s.i}
                placeholder="Customer ID"
              />
            </div>
          </div>
        </div>
        {/* ACCOUNT CLASSIFICATION */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Account classification</h3>
          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>Customer Type *</label>
              <select
                name="customerType"
                value={form.customerType}
                onChange={e => setForm(f => ({ ...f, customerType: e.target.value as typeof form.customerType }))}
                style={s.i}
              >
                <option value="Retail">Retail — standard list prices</option>
                <option value="Wholesale">Wholesale — auto wholesale pricing on orders</option>
                <option value="Walk-in">Walk-in — counter / cash sales</option>
              </select>
            </div>
            <div style={s.c}>
              <label style={s.l}>Status</label>
              <select
                name="status"
                value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value as typeof form.status }))}
                style={s.i}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>Pricing Tier</label>
              <select
                name="pricingTier"
                value={form.pricingTier}
                onChange={e => setForm(f => ({ ...f, pricingTier: e.target.value as typeof form.pricingTier }))}
                style={s.i}
              >
                <option value="">Standard (use wholesale price)</option>
                <option value="A">Tier A — Best trade price</option>
                <option value="B">Tier B — Standard trade</option>
                <option value="C">Tier C — Entry trade</option>
              </select>
              <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                Maps to product Tier A/B/C prices on orders. Contract prices still override.
              </p>
            </div>
            <div style={s.c}>
              <label style={s.l}>Tags</label>
              <input
                type="text"
                name="tagsInput"
                value={form.tagsInput}
                onChange={handleChange}
                style={s.i}
                placeholder="VIP, Cash & Carry, Depot — comma separated"
              />
              <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                Use tags to segment customers for reporting and filtering.
              </p>
            </div>
            <div style={s.c} />
          </div>
        </div>
        {/* COMPANY INFORMATION */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Company &amp; credit</h3>
          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>Company Name</label>
              <input
                type="text"
                name="companyName"
                value={form.companyName}
                onChange={handleChange}
                style={s.i}
                placeholder="Company Ltd"
              />
            </div>
            <div style={s.c}>
              <label style={s.l}>Company Number</label>
              <input
                type="text"
                name="companyNumber"
                value={form.companyNumber}
                onChange={handleChange}
                style={s.i}
                placeholder="12345678"
              />
            </div>
          </div>
          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>Website</label>
              <input
                type="url"
                name="website"
                value={form.website}
                onChange={handleChange}
                style={s.i}
                placeholder="https://example.com"
              />
            </div>
            <div style={s.c} />
          </div>
          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>Nature of Business (SIC Code)</label>
              <input
                type="text"
                name="sicCode"
                value={form.sicCode}
                onChange={handleChange}
                style={s.i}
                placeholder="e.g., 62020 - IT consultancy"
              />
            </div>
            <div style={s.c}>
              <label style={s.l}>VAT Number</label>
              <input
                type="text"
                name="vatNumber"
                value={form.vatNumber}
                onChange={handleChange}
                style={s.i}
                placeholder="GB123456789"
              />
            </div>
          </div>
          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>Credit Terms (Payment Days)</label>
              <select
                name="creditTerms"
                value={form.creditTerms}
                onChange={e => setForm(f => ({ ...f, creditTerms: parseInt(e.target.value) }))}
                style={s.i}
              >
                <option value={7}>Net 7 (7 days)</option>
                <option value={15}>Net 15 (15 days)</option>
                <option value={30}>Net 30 (30 days)</option>
                <option value={60}>Net 60 (60 days)</option>
                <option value={90}>Net 90 (90 days)</option>
              </select>
            </div>
            <div style={s.c}>
              <label style={s.l}>Credit Limit (£)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.creditLimit}
                onChange={e => setForm(f => ({ ...f, creditLimit: parseFloat(e.target.value) || 0 }))}
                style={s.i}
                placeholder="0.00 — leave at 0 for no limit"
              />
              <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                Maximum outstanding balance allowed for this customer. Set to 0 for no limit.
              </p>
            </div>
          </div>
        </div>
        {/* ADDRESS */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Address</h3>
          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>Address</label>
              <input
                type="text"
                name="address"
                value={form.address}
                onChange={handleChange}
                style={s.i}
                placeholder="Street address"
              />
            </div>
            <div style={s.c}>
              <label style={s.l}>City</label>
              <input
                type="text"
                name="city"
                value={form.city}
                onChange={handleChange}
                style={s.i}
                placeholder="City"
              />
            </div>
          </div>
          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>Postcode</label>
              <input
                type="text"
                name="postcode"
                value={form.postcode}
                onChange={handleChange}
                style={s.i}
                placeholder="SW1A 1AA"
              />
            </div>
            <div style={s.c}>
              <label style={s.l}>Country</label>
              <input
                type="text"
                name="country"
                value={form.country}
                onChange={handleChange}
                style={s.i}
                placeholder="United Kingdom"
              />
            </div>
          </div>
        </div>
        {/* STATEMENT PREFERENCES */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Statement preferences</h3>
          <div style={{ ...s.r, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <label style={{ ...s.l, marginBottom: 4 }}>Auto-Send Monthly Statement</label>
              <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
                Automatically email a branded ledger statement to this customer on the 1st of every month.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="checkbox"
                  id="sendMonthlyStatement"
                  checked={form.sendMonthlyStatement}
                  onChange={e => setForm(f => ({ ...f, sendMonthlyStatement: e.target.checked }))}
                  style={{ width: 20, height: 20, cursor: 'pointer' }}
                />
                <label htmlFor="sendMonthlyStatement" style={{ cursor: 'pointer', fontWeight: 500 }}>
                  Enable Monthly Automation
                </label>
              </div>
            </div>
            {form.sendMonthlyStatement && (
              <div style={{ flex: 1 }}>
                <label style={s.l}>Statement Email Override (Optional)</label>
                <input
                  type="email"
                  name="statementEmailOverride"
                  value={form.statementEmailOverride}
                  onChange={handleChange}
                  style={s.i}
                  placeholder="e.g., accounts@clientcompany.com"
                />
                <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  If blank, the statement will be sent to the primary email: {form.email || 'None'}
                </p>
              </div>
            )}
          </div>
        </div>
        {/* ATTACHMENTS */}
        <div style={{
          background: '#f9fafb',
          padding: 16,
          borderRadius: 10,
          marginBottom: 16,
          border: '1px solid #e5e7eb',
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, color: '#111827' }}>Attachments</div>
          {/* Existing uploaded attachments (edit mode) */}
          {isEdit && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              {attachments.length === 0 && <span style={{ fontSize: 13, color: '#888' }}>No attachments yet.</span>}
              {attachments.map(att => (
                <div key={att.id} style={{ background: '#fff', padding: '6px 12px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #d1d5db' }}>
                  <a href={att.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#3b82f6' }}>📄 {att.filename}</a>
                  <button type="button" onClick={() => deleteAttachment(att.id)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                </div>
              ))}
            </div>
          )}
          {/* Queued files (create mode) */}
          {!isEdit && pendingFiles.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              {pendingFiles.map((f, i) => (
                <div key={f.name + i} style={{ background: '#fff', padding: '6px 12px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #d1d5db' }}>
                  <span style={{ fontSize: 13 }}>📄 {f.name}</span>
                  <button type="button" onClick={() => setPendingFiles(prev => prev.filter((_, idx) => idx !== i))} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                </div>
              ))}
            </div>
          )}
          {/* Upload button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="file"
              id="attachment-file-input"
              style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files?.[0];
                if (!file) return;
                e.target.value = '';
                if (isEdit) {
                  setUploading(true);
                  uploadAttachment(file).finally(() => setUploading(false));
                } else {
                  setPendingFiles(prev => [...prev, file]);
                }
              }}
            />
            <label htmlFor="attachment-file-input" style={{
              display: 'inline-block',
              background: '#0074d9',
              color: 'white',
              padding: '8px 16px',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 13,
            }}>
              Add attachment
            </label>
            {uploading && <span style={{ fontSize: 13, color: '#888' }}>Uploading...</span>}
            {!isEdit && pendingFiles.length === 0 && <span style={{ fontSize: 13, color: '#888' }}>Files will upload when you save.</span>}
          </div>
        </div>
        {/* NOTES */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Notes</h3>
          <div>
            <label style={s.l}>Additional Notes</label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              style={{ ...s.i, minHeight: 100, resize: 'vertical' }}
              placeholder="Any additional information about this customer..."
            />
          </div>
        </div>
        {/* ACTIONS */}
        <div style={s.actions}>
          <button type="submit" disabled={loading} style={{ ...s.submitBtn, opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Saving…' : isEdit ? 'Save changes' : 'Create customer'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/customers/list')}
            style={s.cancelBtn}
          >
            Cancel
          </button>
        </div>
      </form>
    </PageLayout>
  );
};

export default CustomerForm;





